import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import '../App.css';
import UserAudienceSection from './userAudienceSection';
import TopSummaryRow from './topSummaryRow';
import UsersTableSection from './usersTableSection';

import PageLoader from '../pageLoader';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const ANESTHESIA_ASSISTANT_APP_ID = '674997d8c4aaadc4054b3372';
const NERVE_BLOCKS_APP_ID = '5e98254f2d1ce10e9431f525';

const EMPTY_FILTERS = {
  id: '',
  firstName: '',
  lastName: '',
  createdAt: '',
  updatedAt: '',
  email: '',
  country: '',
  platform: '',
  status: '',
  subscriptionType: '',
  isLicenseActive: '',
  profileCompleted: '',
  profession: '',
  ageGroup: '',
  specialty: '',
  institution: '',
  isLogged: '',
  isLoggedAt: '',
  isLoggedUpdatedAt: '',
  deactivatedAccount: '',
  deactivatedAt: '',
  purchaseDate: '',
  expirationDate: '',
  firstSubscriptionDate: '',
};

const UsersDashboard = (props) => {
  const { setLoading, loading, selectedApp } = props;

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [debouncedFilters, setDebouncedFilters] = useState(EMPTY_FILTERS);

  const [summary, setSummary] = useState(null);
  const [audience, setAudience] = useState({ byAgeGroup: [], byCountry: [], byProfession: [] });
  const [filterOptions, setFilterOptions] = useState({ professions: [], ageGroups: [] });
  const [tableLoading, setTableLoading] = useState(false);

  const [columnWidths, setColumnWidths] = useState({
    firstName: 140,
    lastName: 140,
    email: 220,
    country: 120,
    createdAt: 180,
    platform: 120,
    subscriptionType: 200,
    isLicenseActive: 140,
    profileCompleted: 160,
    profession: 160,
    ageGroup: 140,
    specialty: 160,
    institution: 200,
    isLogged: 120,
    isLoggedAt: 180,
    isLoggedUpdatedAt: 180,
    deactivatedAccount: 160,
    deactivatedAt: 180,
    purchaseDate: 180,
    expirationDate: 180,
    firstSubscriptionDate: 180,
  });

  const fetchIdRef = useRef(0);
  const isFirstLoadRef = useRef(true);

  const handleSearchChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const normalizeIso = (v) =>
    typeof v === 'string' ? v.replace(/(\.\d{3})\d+(Z)$/i, '$1$2') : v;

  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(normalizeIso(value));
    if (Number.isNaN(d.getTime())) return '-';
    return format(d, 'yyyy-MM-dd HH:mm:ss');
  };

  const buildQueryParams = useCallback(
    (pageOneBased, limit, filterState, exportAll = false) => {
      const params = new URLSearchParams();
      params.set('app_id', selectedApp);
      params.set('page', String(pageOneBased));
      params.set('limit', String(limit));
      if (exportAll) {
        params.set('export', '1');
      }
      Object.entries(filterState).forEach(([key, value]) => {
        if (value != null && String(value).trim() !== '') {
          params.set(key, String(value));
        }
      });
      return params.toString();
    },
    [selectedApp]
  );

  const fetchDashboardPage = useCallback(
    async ({ pageIndex, limit, filterState, exportAll = false }) => {
      const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      if (!baseUrl) {
        throw new Error('REACT_APP_API_BASE_URL is not configured');
      }

      const query = buildQueryParams(pageIndex + 1, limit, filterState, exportAll);
      const res = await fetch(`${baseUrl}/users-dashboard?${query}`);
      if (!res.ok) {
        throw new Error(`Failed to load users dashboard: ${res.status} ${res.statusText}`);
      }
      return res.json();
    },
    [buildQueryParams]
  );

  const loadPage = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    const isInitial = isFirstLoadRef.current;

    if (isInitial) {
      setLoading(true);
    } else {
      setTableLoading(true);
    }

    try {
      const data = await fetchDashboardPage({
        pageIndex: page,
        limit: rowsPerPage,
        filterState: debouncedFilters,
      });

      if (fetchId !== fetchIdRef.current) {
        return;
      }

      setUsers(Array.isArray(data.users) ? data.users : []);
      setTotal(typeof data.total === 'number' ? data.total : 0);
      if (data.summary) {
        setSummary(data.summary);
      }
      if (data.audience) {
        setAudience({
          byAgeGroup: data.audience.byAgeGroup || [],
          byCountry: data.audience.byCountry || [],
          byProfession: data.audience.byProfession || [],
        });
      }
      if (data.filterOptions) {
        setFilterOptions({
          professions: data.filterOptions.professions || [],
          ageGroups: data.filterOptions.ageGroups || [],
        });
      }
    } catch (err) {
      console.error('Failed to fetch users dashboard:', err);
      if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error fetching users from server');
      }
    } finally {
      if (fetchId === fetchIdRef.current) {
        if (isInitial) {
          isFirstLoadRef.current = false;
          setLoading(false);
        }
        setTableLoading(false);
      }
    }
  }, [debouncedFilters, fetchDashboardPage, page, rowsPerPage, setLoading]);

  // Debounce text filters so we do not hit the server on every keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 400);
    return () => clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    isFirstLoadRef.current = true;
    setPage(0);
    setFilters(EMPTY_FILTERS);
    setDebouncedFilters(EMPTY_FILTERS);
    setSummary(null);
    setUsers([]);
    setTotal(0);
  }, [selectedApp]);

  useEffect(() => {
    if (
      selectedApp !== ANESTHESIA_ASSISTANT_APP_ID &&
      selectedApp !== NERVE_BLOCKS_APP_ID
    ) {
      return;
    }
    loadPage();
  }, [selectedApp, page, rowsPerPage, debouncedFilters, loadPage]);

  const exportUsers = async () => {
    try {
      setTableLoading(true);
      const data = await fetchDashboardPage({
        pageIndex: 0,
        limit: rowsPerPage,
        filterState: debouncedFilters,
        exportAll: true,
      });
      const rows = Array.isArray(data.users) ? data.users : [];
      const exportData = rows.map((u) => ({
        'First Name': u.firstName || '',
        'Last Name': u.lastName || '',
        Email: u.email || '',
        Country: u.country || '',
        'Created At': formatDateTime(u.createdAt),
        'Subscription Type': u.subscriptionType || '',
        'Purchase Date': formatDateTime(u.purchaseDate),
        'Expiration Date': formatDateTime(u.expirationDate),
        'First Subscription Date': formatDateTime(u.firstSubscriptionDate),
        'License Active': u.isLicenseActive ? 'Yes' : 'No',
        'Profile Completed': u.profileCompleted ? 'Yes' : 'No',
        Profession: u.profession || '',
        'Age Group': u.ageGroup || '',
        Specialty: u.specialty || '',
        Institution: u.institution || '',
        Logged: u.isLogged ? 'Yes' : 'No',
        'Logged At': formatDateTime(u.isLoggedAt),
        'Logged Updated At': formatDateTime(u.isLoggedUpdatedAt),
        'Deactivated Account': u.deactivatedAccount ? 'Yes' : 'No',
        'Deactivated At': formatDateTime(u.deactivatedAt),
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
      XLSX.writeFile(workbook, `users_${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    } catch (err) {
      console.error('Export failed:', err);
      if (process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error exporting users');
      }
    } finally {
      setTableLoading(false);
    }
  };

  const summaryValues = useMemo(
    () => ({
      totalUsers: summary?.totalUsers ?? 0,
      subscribedUsers: summary?.subscribedUsers ?? 0,
      subscriptionRate: summary?.subscriptionRate ?? 0,
      profileCompletedCount: summary?.profileCompletedCount ?? 0,
      profileCompletedRate: summary?.profileCompletedRate ?? 0,
      deactivatedAccounts: summary?.deactivatedAccounts ?? 0,
    }),
    [summary]
  );

  const showInitialLoader = loading && !summary;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {showInitialLoader ? (
        <PageLoader />
      ) : (
        <Box sx={{ position: 'relative' }}>
          {tableLoading && <PageLoader />}
          <TopSummaryRow
            totalUsers={summaryValues.totalUsers}
            subscriptionRate={summaryValues.subscriptionRate}
            subscribedUsers={summaryValues.subscribedUsers}
            profileCompletedRate={summaryValues.profileCompletedRate}
            profileCompletedCount={summaryValues.profileCompletedCount}
            deactivatedAccounts={summaryValues.deactivatedAccounts}
          />
          <UserAudienceSection
            ageGroups={audience.byAgeGroup}
            topCountries={audience.byCountry}
            professions={audience.byProfession}
          />
          <UsersTableSection
            exportUsers={exportUsers}
            subscriptionLookupStatus={null}
            filters={filters}
            handleSearchChange={handleSearchChange}
            uniqueProfessions={filterOptions.professions}
            uniqueAgeGroups={filterOptions.ageGroups}
            columnWidths={columnWidths}
            paginated={users}
            formatDateTime={formatDateTime}
            filteredUsersLength={total}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            PAGE_SIZE_OPTIONS={PAGE_SIZE_OPTIONS}
          />
        </Box>
      )}
    </Box>
  );
};

export default UsersDashboard;
